import React, { useEffect } from "react";
import styled from "styled-components";
import { Row, Col } from "react-bootstrap";

import { FORM_FIELD_KEYS } from "../../../../../constants/forms";
import { getFieldProps } from "../../../../../utils/form-fields";
import { validateLLCLegalName } from "../../../../../utils/validators";
import { hexToRGBA } from "../../../../../utils/theme";
import { usePrevious } from "../../../../../hooks";

import TextInput from "../../../controls/TextInput";
import Switch from "../../../controls/Switch";
import RadioGroup from "../../../controls/RadioGroup";
import Select from "../../../controls/Select";

const StyledContainer = styled.div`
  margin-bottom: 24px;
`;

const StyledRow = styled(Row)`
  margin-top: 1rem;
  margin-bottom: 2rem;
  margin-right: 0;
  margin-left: 0;
  padding: 16px;
  border: 2px solid #797c7f;
`;

function LLCInfoBlock({ values = {}, onChange, onMouseEnter, onMouseLeave }) {
  const showHusbandAndWife = values[FORM_FIELD_KEYS.LLCMembers] === "2";
  const showMultiMemberGroup = values[FORM_FIELD_KEYS.husbandAndWife] === "true";
  const prevValues = usePrevious(values);

  useEffect(() => {
    if (prevValues && prevValues[FORM_FIELD_KEYS.organizedState] !== values[FORM_FIELD_KEYS.organizedState]) {
      validateLLCLegalName(
        { target: { id: FORM_FIELD_KEYS.legalName, value: values[FORM_FIELD_KEYS.legalName] || "" } },
        { matchValue: values[FORM_FIELD_KEYS.organizedState] }
      );
    }
  }, [prevValues, values]);

  return (
    <StyledContainer>
      <div className="inputs-block-title">
        <span>Legal information</span>
      </div>
      <StyledRow>
        <Col xs={12} md={6}>
          <TextInput
            {...getFieldProps(FORM_FIELD_KEYS.legalName)}
            label="Legal Name of the LLC"
            defaultInvalidMessage="Please provide a valid organization name"
            hint={
              <>
                The only punctuation and special characters allowed are hyphen (-) and ampersand (&). The legal name may
                not contain any of the following endings: Corp, Inc, PA.
              </>
            }
            pattern="(?!.*(?:[ ][pP][aA]$))(?!.*(?:[ ][cC][oO][rR][pP]$))(?!.*(?:[ ][iI][nN][cC]$))(?!.*(?:[ ][iI][nN][cC][oO][rR][pP][oO][rR][aA][tT][eE][dD]$))^[ a-zA-Z0-9&\\-]+$"
            validate={validateLLCLegalName}
            value={values[FORM_FIELD_KEYS.legalName]}
            matchValue={values[FORM_FIELD_KEYS.organizedState]}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
        </Col>
        <Col xs={12} md={6}>
          <TextInput
            {...getFieldProps(FORM_FIELD_KEYS.dbaname)}
            label={getFieldProps(FORM_FIELD_KEYS.dbaname)?.placeholder?.replace("*", "")}
            placeholder={undefined}
            value={values[FORM_FIELD_KEYS.dbaname]}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
        </Col>
        <Col xs={12} md={6}>
          <TextInput
            {...getFieldProps(FORM_FIELD_KEYS.LLCMembers)}
            value={values[FORM_FIELD_KEYS.LLCMembers]}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
        </Col>
        <Col xs={12} md={6}>
          <Select
            {...getFieldProps(FORM_FIELD_KEYS.organizedState)}
            label={getFieldProps(FORM_FIELD_KEYS.organizedState)?.placeholder?.replace("*", "")}
            value={values[FORM_FIELD_KEYS.organizedState]}
            validated={values.validated}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
        </Col>
        {showHusbandAndWife && (
          <Col xs={12}>
            <Switch
              {...getFieldProps(FORM_FIELD_KEYS.husbandAndWife)}
              value={values[FORM_FIELD_KEYS.husbandAndWife]}
              onChange={onChange}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            />
          </Col>
        )}
        {showMultiMemberGroup && (
          <>
            <Col xs={12}>
              <div className="input-title">
                <span>Please choose one of the following:</span>
              </div>
            </Col>
            <Col xs={12}>
              <RadioGroup
                {...getFieldProps(FORM_FIELD_KEYS.multiMember)}
                value={values[FORM_FIELD_KEYS.multiMember]}
                onChange={onChange}
              />
            </Col>
          </>
        )}
        <Col xs={12}>
          <div className="input-title">
            <span>Select the Tax classification of the LLC:</span>
          </div>
        </Col>
        <Col xs={12}>
          <RadioGroup
            {...getFieldProps(FORM_FIELD_KEYS.taxClassification)}
            value={values[FORM_FIELD_KEYS.taxClassification]}
            onChange={onChange}
          />
        </Col>
      </StyledRow>
    </StyledContainer>
  );
}

export default LLCInfoBlock;
